// useScanUploadModal.ts
import { useEffect, useState } from 'react'
import { UploadProps } from 'antd'
import axios from 'axios'
import { API_URL, AppConstant } from 'AppConstant'
import { getTodayScanListAsync } from '../../../../redux/flows/scanWeb'
import { useAppDispatch, useAppSelector } from '../../../../redux/store'
import { APIStatus } from '../../../../redux/types'

export function useScanUploadModal(patientInfo: any) {
  const dispatch = useAppDispatch()

  const todayScanStatus = useAppSelector((state) => state.scan.scanListStatus)
  const todayScan = useAppSelector((state) => state.scan.scanList)

  const [fileUploaded, setFileUploaded] = useState(false)
  const [disableUpload, setDisableUpload] = useState(false)
  const [error, setError] = useState('')

  // -----------------------------------------
  // When upload succeeds → refresh list after 3s
  // -----------------------------------------
  useEffect(() => {
    if (!fileUploaded) return
    const timeout = setTimeout(() => {
      setDisableUpload(false)
      dispatch(getTodayScanListAsync({ timezone: 'America/New_York' }))
    }, 3000)
    return () => clearTimeout(timeout)
  }, [fileUploaded])

  // -----------------------------------------
  // Reset fileUploaded when scan list updates
  // -----------------------------------------
  useEffect(() => {
    if (fileUploaded) setFileUploaded(false)
  }, [todayScan])

  // -----------------------------------------
  // Clear visual error after 3s
  // -----------------------------------------
  useEffect(() => {
    if (!error) return
    const timer = setTimeout(() => setError(''), 3000)
    return () => clearTimeout(timer)
  }, [error])

  // -----------------------------------------
  // Upload Triggered → disable UI
  // -----------------------------------------
  const handleFileChange: UploadProps['onChange'] = () => {
    setDisableUpload(true)
  }

  // -----------------------------------------
  // Main S3 Upload Logic
  // -----------------------------------------
  const uploadProps: UploadProps = {
    multiple: true,
    onChange: handleFileChange,
    accept: '.pbi',
    beforeUpload: (file) => {
      const isPbi = file.name.endsWith('.pbi')
      if (!isPbi) {
        alert('Please upload a .pbi file only.')
        return false
      }
      return true
    },

    customRequest: async ({ file }) => {
      let signedUrlResponse
      let uploadImageRes

      // 1️⃣ Generate signed upload URL
      try {
        const res = await axios.post(
          `${API_URL}/scan-request/signed-url-generate`,
          { visitId: patientInfo.visitId },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(AppConstant.ACCESS_TOKEN_SCAN_AUTH)}`,
              'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
            },
          },
        )

        signedUrlResponse = res

        if (res.data.code !== 'success') {
          setError('signedUrlNotFound')
          setDisableUpload(false)
          alert('Could not generate upload URL. Please try again.')
          return
        }
      } catch (err: any) {
        setDisableUpload(false)
        setError('failedToUpload')

        if (err?.code === 'ERR_NETWORK') {
          alert('Network error. Please try again.')
        } else if (err?.response?.status) {
          alert(`Error Code: ${err.response.status}. Please try again.`)
        } else {
          alert('Unexpected error occurred.')
        }
        return
      }

      // 2️⃣ Upload file to S3
      try {
        const uploadUrl = signedUrlResponse?.data?.data?.uploadUrl

        const uploadRes = await axios.put(uploadUrl, file, {
          headers: { 'Content-Type': 'application/octet-stream' },
        })

        uploadImageRes = uploadRes

        if (![200, 201].includes(uploadRes.status)) {
          setError('failedToUpload')
          setDisableUpload(false)
          alert('Failed to upload .pbi file.')
          return
        }
      } catch (err: any) {
        setDisableUpload(false)
        setError('failedToUpload')

        if (err?.code === 'ERR_NETWORK') {
          alert('Network error. Please try again.')
        } else if (err?.response?.status) {
          alert(`Error Code: ${err.response.status}`)
        } else {
          alert('Unexpected error occurred.')
        }
        return
      }

      // 3️⃣ Update scan status
      try {
        const updateStatus = uploadImageRes?.status === 200 || uploadImageRes?.status === 201 ? 'completed' : 'failed'

        const updateRes = await axios.put(
          `${API_URL}/scan-request/visit/${patientInfo.visitId}`,
          { status: updateStatus },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem(AppConstant.ACCESS_TOKEN_SCAN_AUTH)}`,
              'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
            },
          },
        )

        setFileUploaded(true)
      } catch (err: any) {
        setDisableUpload(false)
        setFileUploaded(true)

        if (err?.code === 'ERR_NETWORK') {
          alert('Network error. Please try again.')
        } else if (err?.response?.status) {
          alert(`Error Code: ${err.response.status}`)
        } else {
          alert('Unexpected error occurred.')
        }
      }
    },
  }

  return {
    todayScanStatus,
    fileUploaded,
    disableUpload,
    error,
    uploadProps,
    handleFileChange,
    APIStatus,
  }
}
